import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Drawer, FormField, Input, SegmentedTabs, toast } from '@/ui';
import { LEGAL_COPY } from '@/features/legal/legal-copy';
import { refineLocation, type TrLocation } from '@/lib/locations';
import { LocationFields } from '@/features/locations/LocationFields';
import { useLocations } from '@/features/locations/useLocations';
import { createOrganization, type ProvisionResult } from './api';
import { invalidateAfterOrganizationMutation } from './invalidation';
import { adminErrorMessage } from './error-message';
import styles from './Admin.module.css';

/**
 * ============ ISLETME + KURUCU YONETICISI OLUSTURMA (/yonetim/isletmeler) ============
 *
 * Self-servis kayit 2026-08-30'da kapatildi; organizasyon yaratmanin TEK yolu bu cekmece.
 * Sunucu dordunu tek transaction'da yazar: organizasyon, kurucu kullanici, rol satiri
 * (olmazsa kullanici HIC giris yapamaz) ve admin uyeligi.
 *
 * TIP SECIMI EKRANIN OMURGASI:
 *   * 'exchange' (doviz burosu) -> oda + sicil ZORUNLU (sunucu da 400 doner)
 *   * 'customer' (musteri)      -> oda/sicil kavrami YOK, alanlar hic gosterilmez
 * Alanlari her iki tipte de gostermek, musteri kaydinda doldurulmasi gereken bir sey
 * varmis izlenimi verirdi.
 *
 * GECICI SIFRE: yalnizca e-posta GIDEMEDIGINDE ve YALNIZCA BIR KEZ gelir; sunucu bir daha
 * vermez. Bu yuzden kapatilabilir bir bildirimde degil, kalici bir panelde gosteriliyor
 * (InviteMemberDrawer ile ayni sozlesme ve ayni gerekce).
 */

type OrganizationTypeCode = 'customer' | 'exchange';

const makeSchema = (t: TFunction, locations: readonly TrLocation[] = []) =>
  z
    .object({
      organizationName: z.string().trim().min(1, t('validation.fullNameRequired')),
      founderFullName: z.string().trim().min(1, t('validation.fullNameRequired')),
      founderEmail: z.string().trim().email(t('validation.emailInvalid')),
      businessChamber: z.string().trim().optional(),
      businessNumber: z.string().trim().optional(),
      // Il/ilce istege bagli; DOLUYSA listeden (M4). Isletme profilinden sonradan da girilebilir.
      organizationCity: z.string(),
      organizationDistrict: z.string(),
    })
    .superRefine(
      refineLocation({
        locations,
        cityField: 'organizationCity',
        districtField: 'organizationDistrict',
        message: t('locations.pickFromList'),
      }),
    );

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

type CreateOrganizationDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** Basari panelindeki "IBAN ekle" baglantisi icin: yeni isletmeye gitmek. */
  onCreated?: () => void;
};

export function CreateOrganizationDrawer({ open, onClose, onCreated }: CreateOrganizationDrawerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [typeCode, setTypeCode] = useState<OrganizationTypeCode>('customer');
  const [created, setCreated] = useState<ProvisionResult | null>(null);
  const [createdEmail, setCreatedEmail] = useState('');

  const locations = useLocations();
  const schema = useMemo(() => makeSchema(t, locations), [t, locations]);
  const {
    control,
    register,
    setValue,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      organizationName: '',
      founderFullName: '',
      founderEmail: '',
      businessChamber: '',
      businessNumber: '',
      organizationCity: '',
      organizationDistrict: '',
    },
  });

  const create = useMutation({
    mutationFn: createOrganization,
    onSuccess: async (result) => {
      setCreated(result);
      setCreatedEmail(getValues('founderEmail').trim());
      reset();
      await invalidateAfterOrganizationMutation(queryClient);
      if (result.emailDelivered) {
        toast({ message: t('admin.organizations.created'), tone: 'success' });
      }
      onCreated?.();
    },
    onError: (error) => {
      toast({ message: adminErrorMessage(error, t), tone: 'danger' });
    },
  });

  const onSubmit = handleSubmit((values) => {
    // Oda/sicil YALNIZ buro icin gonderilir. Musteri kaydinda bos dize gondermek,
    // sunucuda anlamsiz bos degerler birakirdi (kolonlar NULL kabul ediyor).
    const business =
      typeCode === 'exchange'
        ? {
            businessChamber: values.businessChamber?.trim() ?? '',
            businessNumber: values.businessNumber?.trim() ?? '',
          }
        : {};
    // Il/ilce bos birakildiysa HIC gonderilmez (kolon NULL kalir), oda/sicil ile ayni gerekce.
    const location =
      values.organizationCity !== ''
        ? {
            organizationCity: values.organizationCity,
            ...(values.organizationDistrict !== ''
              ? { organizationDistrict: values.organizationDistrict }
              : {}),
          }
        : {};
    create.mutate({
      organizationName: values.organizationName.trim(),
      organizationTypeCode: typeCode,
      founderFullName: values.founderFullName.trim(),
      founderEmail: values.founderEmail.trim(),
      ...business,
      ...location,
    });
  });

  const close = (): void => {
    // Gecici sifre EKRANDAN KAYBOLURSA bir daha alinamaz; panel kapanirken durum
    // temizleniyor cunku bir sonraki acilisin onu gostermesi yanlis olurdu.
    setCreated(null);
    setCreatedEmail('');
    reset();
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title={t('admin.organizations.createTitle')}
      footer={
        <>
          <Button tone="ghost" onClick={close}>
            {created ? t('members.done') : t('auth.give')}
          </Button>
          <Button loading={create.isPending} onClick={() => void onSubmit()}>
            {t('admin.organizations.createAction')}
          </Button>
        </>
      }
    >
      <form className={styles.composer} onSubmit={(event) => void onSubmit(event)} noValidate>
        <div>
          <p className={styles.sectionTitle}>{t('admin.organizations.typeLabel')}</p>
          <SegmentedTabs<OrganizationTypeCode>
            label={t('admin.organizations.typeLabel')}
            value={typeCode}
            onChange={setTypeCode}
            items={[
              { value: 'customer', label: t('admin.organizations.typeCustomer') },
              { value: 'exchange', label: t('admin.organizations.typeExchange') },
            ]}
          />
        </div>

        <FormField label={t('admin.organizations.nameLabel')} error={errors.organizationName} required>
          <Input autoComplete="off" {...register('organizationName')} />
        </FormField>

        <LocationFields
          control={control}
          register={register}
          setValue={setValue}
          cityName="organizationCity"
          districtName="organizationDistrict"
          cityLabel={t('org.profile.city')}
          districtLabel={t('org.profile.district')}
        />

        {/* Oda/sicil YALNIZ doviz burosunda: musteri organizasyonunda boyle bir kayit yok. */}
        {typeCode === 'exchange' ? (
          <>
            <FormField label={t('admin.organizations.chamberLabel')} error={errors.businessChamber} required>
              <Input autoComplete="off" {...register('businessChamber')} />
            </FormField>
            <FormField label={t('admin.organizations.numberLabel')} error={errors.businessNumber} required>
              <Input autoComplete="off" {...register('businessNumber')} />
            </FormField>
          </>
        ) : null}

        <p className={styles.sectionTitle}>{t('admin.organizations.founderSection')}</p>
        <p className={styles.muted}>{t('admin.organizations.founderHint')}</p>

        <FormField label={t('org.invite.fullName')} error={errors.founderFullName} required>
          <Input autoComplete="off" {...register('founderFullName')} />
        </FormField>

        <FormField label={t('org.invite.email')} error={errors.founderEmail} required>
          <Input type="email" inputMode="email" autoComplete="off" {...register('founderEmail')} />
        </FormField>

        {/* Yoneticiye, BASKASININ verisini girdigini hatirlatir (KVKK md.10). */}
        <p className={styles.warningNotice}>{LEGAL_COPY.organization.inviteAdminWarning}</p>

        {created && !created.emailDelivered && created.tempPassword ? (
          <div className={styles.warningNotice}>
            <p>{t('org.invite.createdNoEmail')}</p>
            <p>{createdEmail}</p>
            <code className={styles.tempPassword}>{created.tempPassword}</code>
            <p>{LEGAL_COPY.organization.inviteNotice}</p>
          </div>
        ) : null}

        {created?.emailDelivered ? (
          <p className={styles.notice}>{t('org.invite.passwordEmailed')}</p>
        ) : null}

        {/* IBAN'i olmayan bir isletmede musteri TALEP ACAMAZ (hesap secimi zorunlu).
            Yaratma anindaki en olasi sonraki adim bu; hatirlatilmazsa isletme sessizce
            kullanilamaz durumda kalir. */}
        {created ? <p className={styles.notice}>{t('admin.organizations.nextAddIban')}</p> : null}
      </form>
    </Drawer>
  );
}
